import {
  Add as AddIcon,
  People as PeopleIcon,
  BarChart as SalesIcon,
  Checklist as TasksIcon,
  Twitter as TwitterIcon,
  Article as ArticleIcon,
  LinkedIn as LinkedInIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { useEffect, useState, memo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import logoWithName from '../assets/blocsys_logo.svg';
import Cookies from 'js-cookie';
import LogoutIcon from '@mui/icons-material/Logout';
import backendService from './backendservices/BackendService';
import { motion } from 'framer-motion';

const drawerWidth = 260; // Slightly wider as per image

// Stability outside component
const MotionListItem = motion(ListItemButton);

const navItemVariants = {
  hidden: { x: -10, opacity: 0 },
  visible: (i: number) => ({
    x: 0,
    opacity: 1,
    transition: {
      delay: i * 0.04,
      duration: 0.4,
      ease: [0.25, 1, 0.5, 1] as any,
    },
  }),
};

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    setHasLoaded(true);
  }, []);

  // Items matches the Image structure
  const mainItems = [
    { text: 'Dashboard', path: '/dashboard', icon: <SalesIcon />, iconColor: '#6871d1' },
    { text: 'Contacts', path: '/contacts', icon: <PeopleIcon />, iconColor: '#8e94bb' },
  ];

  const operationsItems = [
    { text: 'Task Tracking', path: '/content-tracking', icon: <TasksIcon /> },
    { text: 'Blog Checklist', path: '/blog-checklist', icon: <ArticleIcon /> },
    { text: 'Linkedin Checklist', path: '/linkedin-post-checklist', icon: <LinkedInIcon /> },
  ];

  const insightItems = [
    { text: 'Twitter Dashboard', path: '/twitter-dashboard', icon: <TwitterIcon /> },
  ];

  const systemItems = [{ text: 'Admin', path: '/add-admin', icon: <AddIcon /> }];

  const isActive = (path: string) => location.pathname === path;

  const username = Cookies.get('username');
  const userRole = Cookies.get('role');
  const isAdmin = userRole === 'admin' || userRole === 'superadmin';

  const getInitials = (user: string | undefined): string => {
    if (!user) return 'U';
    return user.substring(0, 2).toUpperCase();
  };

  const userInitials = getInitials(username);

  const [currentUser, setCurrentUser] = useState<{
    lastLogin?: string;
    lastLogout?: string;
    activeTime?: number;
    loginTimestamps?: string[];
  } | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      if (!isAdmin) return;
      try {
        const response = await backendService.Get(
          `${process.env.REACT_APP_BASE_URL}/auth/admin/users?page=1`
        );
        if (response.status === 200 && Array.isArray(response.data)) {
          const user = response.data.find((u: any) => (u.username || u.email) === username);
          if (user) setCurrentUser(user);
        }
      } catch (e) {
        console.error('Sidebar Data Error:', e);
      }
    };
    if (username) fetchUser();
  }, [username, isAdmin]);

  const renderNavSection = (items: any[], startIndex: number) =>
    items.map((item, index) => {
      const active = isActive(item.path);
      return (
        <MotionListItem
          key={item.text}
          custom={startIndex + index}
          initial={hasLoaded ? false : 'hidden'}
          animate='visible'
          variants={navItemVariants}
          whileHover={{ x: 4 }}
          sx={{
            borderRadius: '10px',
            mx: 1.5,
            mb: 0.8,
            py: 1.4,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            background: active ? 'rgba(104, 113, 209, 0.15)' : 'transparent',
            '&:hover': {
              background: active ? 'rgba(104, 113, 209, 0.2)' : 'rgba(255, 255, 255, 0.03)',
            },
          }}
          onClick={() => navigate(item.path)}
        >
          <ListItemIcon
            sx={{
              minWidth: 42,
              color: active ? '#6871d1' : 'rgba(142, 148, 187, 0.6)',
              '& svg': { fontSize: 22 },
            }}
          >
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography
                variant='body2'
                sx={{
                  fontWeight: active ? 700 : 500,
                  fontSize: '0.88rem',
                  color: active ? '#fff' : 'rgba(142, 148, 187, 0.7)',
                  fontFamily: "'Inter', sans-serif",
                  letterSpacing: '0.3px',
                }}
              >
                {item.text}
              </Typography>
            }
          />
          {active && (
            <Box
              sx={{
                width: 4,
                height: 4,
                borderRadius: '2px',
                background: '#6871d1',
                boxShadow: '0 0 10px #6871d1',
              }}
            />
          )}
        </MotionListItem>
      );
    });

  return (
    <Drawer
      variant='permanent'
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          background: 'linear-gradient(135deg, #1a1b2e 0%, #111222 100%)',
          color: '#fff',
          border: 'none',
          borderRight: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
        },
      }}
    >
      {/* Dynamic Animated Iridescent Glows */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.1, 0.2, 0.1],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
        style={{
          position: 'absolute',
          top: '-10%',
          right: '-20%',
          width: '120%',
          height: '50%',
          background: 'radial-gradient(circle, rgba(139, 147, 255, 0.25) 0%, transparent 70%)',
          zIndex: 0,
          filter: 'blur(50px)',
          pointerEvents: 'none',
        }}
      />
      <motion.div
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.05, 0.15, 0.05],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 2,
        }}
        style={{
          position: 'absolute',
          bottom: '10%',
          left: '-30%',
          width: '140%',
          height: '40%',
          background: 'radial-gradient(circle, rgba(0, 242, 254, 0.1) 0%, transparent 70%)',
          zIndex: 0,
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />

      {/* Brand Section with Pulse Aura */}
      <Box
        sx={{
          p: '40px 30px 40px',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <motion.div
          animate={{
            opacity: [0.3, 0.6, 0.3],
            scale: [1, 1.05, 1],
          }}
          transition={{ duration: 4, repeat: Infinity }}
          style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle, rgba(104, 113, 209, 0.2) 0%, transparent 70%)',
            zIndex: -1,
            filter: 'blur(20px)',
          }}
        />
        <Typography
          variant='h3'
          component={motion.h3}
          animate={{
            backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'linear',
          }}
          sx={{
            fontWeight: 900,
            fontFamily: "'Outfit', sans-serif",
            fontSize: '2.8rem',
            // Exact gradient from Dashboard welcome text
            background: 'linear-gradient(90deg, #fff, #a2a8ff, #fff, #8b93ff, #fff)',
            backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-2px',
            lineHeight: 1.2,
            filter: 'drop-shadow(0 0 30px rgba(139, 147, 255, 0.15))',
            willChange: 'background-position',
            cursor: 'pointer',
          }}
          onClick={() => navigate('/dashboard')}
        >
          Scrapify
        </Typography>
      </Box>

      {/* Main Navigation */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          position: 'relative',
          zIndex: 1,
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <List sx={{ pt: 1 }}>
          {renderNavSection(mainItems, 0)}
          {renderNavSection(operationsItems, mainItems.length)}
          {renderNavSection(insightItems, mainItems.length + operationsItems.length)}

          {isAdmin &&
            renderNavSection(
              systemItems,
              mainItems.length + operationsItems.length + insightItems.length
            )}
        </List>
      </Box>

      {/* User & Footer Area (Match Image) */}
      <Box sx={{ p: 2, pb: 4 }}>
        {/* Last Login Glass Card */}
        <Box
          sx={{
            background: 'rgba(255, 255, 255, 0.04)',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            borderRadius: '16px',
            p: 2,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: '10px',
              background: 'rgba(104, 113, 209, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6871d1',
            }}
          >
            <TimeIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography
              sx={{ fontSize: '0.65rem', color: 'rgba(142, 148, 187, 0.5)', fontWeight: 500 }}
            >
              Last Login
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#fff' }}>
              {currentUser?.lastLogin
                ? new Date(currentUser.lastLogin).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : '34m ago'}
            </Typography>
          </Box>
        </Box>

        {/* Profile/Role Header Area */}
        <Box
          sx={{
            background: 'rgba(255, 255, 255, 0.04)',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            borderRadius: '16px',
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Avatar
            sx={{
              width: 38,
              height: 38,
              background: 'linear-gradient(135deg, #6871d1 0%, #4e54c8 100%)',
              color: '#fff',
              fontSize: '0.8rem',
              fontWeight: 800,
              borderRadius: '10px',
            }}
          >
            {userInitials}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '0.9rem',
                fontWeight: 700,
                color: '#fff',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {username || 'Unknown'}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.7rem',
                color: 'rgba(142, 148, 187, 0.5)',
                fontWeight: 500,
                textTransform: 'capitalize',
              }}
            >
              {userRole || 'User'}
            </Typography>
          </Box>
          <LogoutIcon
            onClick={() => {
              Cookies.remove('token', { path: '/' });
              Cookies.remove('role', { path: '/' });
              Cookies.remove('username', { path: '/' });
              localStorage.clear();
              sessionStorage.clear();
              navigate('/', { replace: true });
            }}
            sx={{
              fontSize: 18,
              color: '#ff4d4f',
              cursor: 'pointer',
              opacity: 0.6,
              '&:hover': { opacity: 1 },
            }}
          />
        </Box>

        {/* Branding Bottom */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mt: 3,
            opacity: 0.3,
          }}
        >
          <img
            src={logoWithName}
            alt='Logo'
            style={{ width: '90px', filter: 'grayscale(1) brightness(2)' }}
          />
          <Typography sx={{ fontSize: '0.6rem', mt: 0.8, fontWeight: 600, letterSpacing: '0.5px' }}>
            © {new Date().getFullYear()} BLOCSYS
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
};

export default memo(Sidebar);
